import {
  signIn,
  signOut,
  getCurrentUser,
  confirmSignIn,
  fetchAuthSession,
  fetchUserAttributes,
  resetPassword,
  confirmResetPassword,
  updatePassword,
} from '@aws-amplify/auth';
import {
  createAsyncThunk,
  createSlice,
  Action,
  PayloadAction,
  UnknownAction,
} from '@reduxjs/toolkit';
import { Amplify } from 'aws-amplify';

import { Hub, sessionStorage } from 'aws-amplify/utils';

import AuthService, { UpdateMFAPreferenceType } from '../../../api/services/AuthService';
import { AMPLIFY_AUTH_CONFIG } from '../../../config';
import {
  removeAccessTokenCookie,
  removeLastActivityCookie,
  setAccessTokenCookie,
  upsertLastActivityCookie,
} from '../../../utils/sessionCookies';

import { RootState } from '../../../store';
import { clearAccountState, getMeThunk } from '../../account/api/accountSlice';
import { clearGetCareState } from '../../get-care/api/getCareSlice';
import { checkIfUserHasPermission } from '../../../hooks/useAuth';

Amplify.configure({
  Auth: {
    Cognito: {
      identityPoolId: AMPLIFY_AUTH_CONFIG.IDENTITY_POOL_ID,
      userPoolId: AMPLIFY_AUTH_CONFIG.USER_POOL_ID,
      userPoolClientId: AMPLIFY_AUTH_CONFIG.USER_POOL_WEB_CLIENT_ID,
      loginWith: {
        oauth: {
          domain: AMPLIFY_AUTH_CONFIG.OAUTH.DOMAIN,
          scopes: AMPLIFY_AUTH_CONFIG.OAUTH.SCOPE,
          redirectSignIn: [AMPLIFY_AUTH_CONFIG.OAUTH.REDIRECT_SIGN_IN],
          redirectSignOut: [AMPLIFY_AUTH_CONFIG.OAUTH.REDIRECT_SIGN_OUT],
          responseType: 'code',
        },
      },
    },
  },
});

export const clearCognitoStorage = () => {
  Object.keys(localStorage).forEach((key) => {
    if (key.includes('CognitoIdentityServiceProvider')) {
      localStorage.removeItem(key);
    }
  });

  sessionStorage.clear();
};

export interface AuthState {
  username: string;
  email?: string;
  emailVerified?: boolean;
  familyName?: string;
  givenName?: string;
  idToken?: string;
  accessToken?: string;
  preferredUsername?: string;
  needsVerification?: boolean;
  status: 'pending' | 'success' | 'failure';
  newPasswordRequired?: boolean;
  isLoading: boolean;
  isInactive?: boolean;
  error?: string;
  errorName?: string;
  mfaRequired?: boolean;
  errorMessage?: string;
}

const initialState: AuthState = {
  username: '',
  email: '',
  emailVerified: true,
  idToken: '',
  accessToken: '',
  givenName: '',
  familyName: '',
  preferredUsername: '',
  needsVerification: false,
  newPasswordRequired: false,
  status: 'pending',
  isLoading: false,
  isInactive: false,
  error: '',
  errorName: '',
  errorMessage: '',
};

const ACTIONS_PREFIX = 'auth';

const INVALID_CREDENTIALS_ERROR = 'Invalid username or password.';

const getInvalidCredentialsError = (error: Error): Partial<AuthState> => ({
  errorName: error.name,
  errorMessage: error.message,
  error: INVALID_CREDENTIALS_ERROR,
});

const getGeneralSignInError = (error: Error): Partial<AuthState> => ({
  idToken: '',
  accessToken: '',
  username: '',
  status: 'failure',
  isLoading: false,
  error: 'Something went wrong. Please try again.',
  errorName: error.name,
  errorMessage: error.message,
});

const getHttpStatus = (error: any) => error?.response?.status || error?.statusCode || error?.status;

const getAuthStateError = (error: Error) =>
  (error as Error & { authState?: Partial<AuthState> }).authState;

const isInvalidCredentialsError = (error: Error) =>
  error.name === 'UserNotFoundException' || error.name === 'NotAuthorizedException';

const signInAndBuildAuthState = async ({
  username,
  password,
  displayUsername = username,
  keepDisplayUsername = false,
}: {
  username: string;
  password: string;
  displayUsername?: string;
  keepDisplayUsername?: boolean;
}): Promise<Partial<AuthState>> => {
  const { isSignedIn, nextStep } = await signIn({
    username,
    password,
  });

  if (!isSignedIn) {
    if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return {
        username: displayUsername,
        isLoading: false,
        newPasswordRequired: true,
        status: 'pending',
      };
    } else if (
      nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' ||
      nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE'
    ) {
      return {
        username: displayUsername,
        isLoading: false,
        mfaRequired: true,
        status: 'pending',
      };
    } else {
      throw new Error('Sign-in was not completed');
    }
  }

  const user = await getCurrentUser();
  const userAttributes: any = await fetchUserAttributes();

  const identifierFromChallenge = userAttributes?.['custom:identifier'];

  if (identifierFromChallenge) {
    const hasPermission = checkIfUserHasPermission(identifierFromChallenge);
    if (!hasPermission) {
      clearCognitoStorage();
      const error = new Error('User not found or invalid credentials') as Error & {
        authState: Partial<AuthState>;
      };
      error.name = 'UserNotFoundException';
      error.authState = {
        errorName: 'UserNotFoundException',
        errorMessage: 'User not found or invalid credentials',
        error: 'User not found or invalid credentials',
      };
      throw error;
    }
  }
  const { accessToken, idToken } = (await fetchAuthSession()).tokens ?? {};

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { email, email_verified, preferred_username, family_name, given_name } = userAttributes;
  return {
    username: keepDisplayUsername ? displayUsername : user.username,
    idToken: idToken?.toString(),
    accessToken: accessToken?.toString(),
    email: keepDisplayUsername ? displayUsername : email,
    emailVerified: email_verified,
    preferredUsername: preferred_username,
    familyName: family_name,
    givenName: given_name,
    status: 'success',
  };
};

export const createSessionApiThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/createSessionApi`,
  async (params: { accessToken: string }, { rejectWithValue, dispatch }) => {
    const joinWeirdErrorMessage = (error: any) => {
      /*
       * This only happens if the account is suspended
       */
      const integerKeys = Object.keys(error).filter((key) => Number.isInteger(parseInt(key)));
      const isWeirdResponse = integerKeys.length > 10;

      if (isWeirdResponse) {
        return Object.keys(error)
          .filter((key) => Number.isInteger(parseInt(key)))
          .map((key) => error[key])
          .join('');
      }
      return '';
    };

    try {
      const createSessionResult = await AuthService.createSession(params);
      if (
        !createSessionResult.data ||
        Object.keys(createSessionResult.data).length === 0 ||
        createSessionResult.data.statusCode !== 200
      ) {
        throw new Error('Could not create session!');
      }
      setAccessTokenCookie(createSessionResult.data.resource);
      await dispatch(getMeThunk()).unwrap();
      upsertLastActivityCookie();
    } catch (err) {
      const error = err as Error;
      const message = joinWeirdErrorMessage(error);
      return rejectWithValue({
        errorName: error.name,
        errorMessage: error.message,
        error:
          typeof err === 'string' ? err : message || error?.message || 'Could not create session!',
      } as Partial<AuthState>);
    }
  },
);

export const createWSSessionAPIThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/createWSSessionAPI`,
  async (params: { deviceId: string }, { rejectWithValue }) => {
    try {
      const createWSSessionResult = await AuthService.createWSSession(params);
      if (
        !createWSSessionResult.data ||
        Object.keys(createWSSessionResult.data).length === 0 ||
        createWSSessionResult.data.statusCode !== 200
      ) {
        throw new Error('Could not create WS session!');
      }
      const { wsToken, expiresAt } = createWSSessionResult.data.resource;
      return {
        token: wsToken,
        expiresAt,
      };
    } catch (err) {
      const error = err as Error;
      return rejectWithValue({
        errorName: error.name,
        errorMessage: error.message,
        error: typeof err === 'string' ? err : error?.message || 'Could not create WS session!',
      } as Partial<AuthState>);
    }
  },
);

export const updateMFAPreferenceThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/updateMFAPreference`,
  async (params: UpdateMFAPreferenceType) => {
    return await AuthService.updateMfaPreferenceService(params);
  },
);

export const loginUserThunk = createAsyncThunk<
  Partial<AuthState>,
  { username: string; password: string }
>(`${ACTIONS_PREFIX}/loginUser`, async (action, { rejectWithValue }) => {
  try {
    return await signInAndBuildAuthState({
      username: action.username,
      password: action.password,
    });
  } catch (err: unknown) {
    const error = err as Error;
    const authStateError = getAuthStateError(error);
    if (authStateError) {
      return rejectWithValue(authStateError);
    }

    if (error.name === 'NotAuthorizedException') {
      try {
        const resolveResult = await AuthService.resolveCognitoUsername({ email: action.username });
        const cognitoUsername = resolveResult.data?.resource?.cognitoUsername;

        if (!cognitoUsername) {
          return rejectWithValue(getInvalidCredentialsError(error));
        }

        try {
          return await signInAndBuildAuthState({
            username: cognitoUsername,
            password: action.password,
            displayUsername: action.username,
            keepDisplayUsername: true,
          });
        } catch (retryErr: unknown) {
          const retryError = retryErr as Error;
          const retryAuthStateError = getAuthStateError(retryError);

          if (retryAuthStateError) {
            return rejectWithValue(retryAuthStateError);
          }

          if (isInvalidCredentialsError(retryError)) {
            return rejectWithValue(getInvalidCredentialsError(retryError));
          }

          return rejectWithValue(getGeneralSignInError(retryError));
        }
      } catch (resolveErr: unknown) {
        const resolveError = resolveErr as Error;

        if (getHttpStatus(resolveErr) === 404) {
          return rejectWithValue(getInvalidCredentialsError(error));
        }

        return rejectWithValue(getGeneralSignInError(resolveError));
      }
    }

    if (error.name === 'UserNotFoundException') {
      return rejectWithValue(getInvalidCredentialsError(error));
    }

    return rejectWithValue(getGeneralSignInError(error));
  }
});

export const logoutUserThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/logoutUser`,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  async (_, { fulfillWithValue, dispatch }) => {
    try {
      // global: true, signs out all sessions
      await signOut({ global: true });
    } catch (err) {
      const error = err as Error;
      console.log(':: COGNITO LOGOUT ERROR ::', error);
      // * Force signOut event when cognito errors out
      Hub.dispatch('auth', { event: 'signOut' });
    } finally {
      dispatch(clearAccountState());
      dispatch(clearGetCareState());
      removeAccessTokenCookie();
      removeLastActivityCookie();
      fulfillWithValue(true);
    }
  },
);

export const completeNewPasswordThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/newPasswordRequiredUser`,
  async ({ newPassword }: { newPassword: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { username } = state.auth;

      const { isSignedIn, nextStep } = await confirmSignIn({
        challengeResponse: newPassword,
      });

      if (!isSignedIn) {
        if (
          nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' ||
          nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE'
        ) {
          return {
            username,
            isLoading: false,
            mfaRequired: true,
            status: 'pending',
          } as AuthState;
        } else {
          throw new Error('Sign-in was not completed');
        }
      }

      const userAttributes = await fetchUserAttributes();
      const { accessToken, idToken } = (await fetchAuthSession()).tokens ?? {};

      return {
        username,
        idToken: idToken?.toString(),
        accessToken: accessToken?.toString(),
        ...userAttributes,
        isLoading: false,
        status: 'success',
      } as AuthState;
    } catch (err) {
      const error = err as Error;
      return rejectWithValue({
        errorName: error.name,
        errorMessage: error.message,
        error: 'Something went wrong. Please try again.',
      } as Partial<AuthState>);
    }
  },
);

export const updatePasswordThunk = createAsyncThunk<
  any,
  { oldPassword: string; newPassword: string }
>(`${ACTIONS_PREFIX}/forgotPasswordRequest`, async (action, { rejectWithValue }) => {
  try {
    await updatePassword({
      oldPassword: action.oldPassword,
      newPassword: action.newPassword,
    });
  } catch (err: unknown) {
    const error = err as Error;
    if (error.name === 'LimitExceededException') {
      return rejectWithValue({
        errorName: error.name,
        errorMessage: 'Attempt limit exceeded, please try again later.',
        error: 'Cannot request password reset at this time.',
      });
    }
    return rejectWithValue({
      errorName: error.name,
      errorMessage: error.message,
      error: 'Something went wrong. Cannot request password reset.',
    });
  }
});

export const resetPasswordThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/resetPassword`,
  async (
    { username, code, newPassword }: { username: string; code: string; newPassword: string },
    { rejectWithValue },
  ) => {
    try {
      await confirmResetPassword({
        username,
        confirmationCode: code,
        newPassword,
      });
      return {
        username,
        isLoading: false,
        status: 'success',
      } as AuthState;
    } catch (err) {
      const error = err as Error;
      return rejectWithValue({
        errorName: error.name,
        errorMessage: error.message,
        error: 'Something went wrong. Please try again.',
      } as Partial<AuthState>);
    }
  },
);
export const forgotPasswordThunk = createAsyncThunk<Partial<AuthState>, { username: string }>(
  `${ACTIONS_PREFIX}/forgotPassword`,
  async (action, { rejectWithValue }) => {
    try {
      await resetPassword({ username: action.username });
      return {};
    } catch (err: unknown) {
      const error = err as Error;
      return rejectWithValue({
        errorName: error.name,
        errorMessage: error.message,
        error: 'Something went wrong. Please try again.',
      } as Partial<AuthState>);
    }
  },
);

export const generateMagicLinkThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/generateMagicLink`,
  async (params: { email: string }) => {
    return await AuthService.generateMagicLink(params);
  },
);

export const verifyMagicLinkThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/verifyMagicLink`,
  async (params: { code: string }) => {
    return await AuthService.verifyMagicLink(params);
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<Partial<AuthState>>) => {
      const { username, accessToken } = action.payload;
      state.username = username || '';
      state.accessToken = accessToken || '';
    },
    setInactivity: (state, action: PayloadAction<{ isInactive: boolean }>) => {
      const { isInactive } = action.payload;
      state.isInactive = isInactive;
    },
    clearAuthState: () => initialState,
  },
  extraReducers: (builder) => {
    // login user thunk fulfilled
    builder.addCase(loginUserThunk.fulfilled, (state, action) => {
      const {
        idToken,
        accessToken,
        preferredUsername,
        newPasswordRequired,
        username,
        email,
        emailVerified,
        familyName,
        givenName,
      } = action.payload;
      state.username = username || '';
      state.email = email || '';
      state.emailVerified = emailVerified || true;
      state.familyName = familyName || '';
      state.givenName = givenName || '';
      state.idToken = idToken || '';
      state.accessToken = accessToken || '';
      state.preferredUsername = preferredUsername || '';
      state.newPasswordRequired = newPasswordRequired || false;
    });
    builder.addCase(completeNewPasswordThunk.fulfilled, (state, action) => {
      const {
        idToken,
        accessToken,
        preferredUsername,
        newPasswordRequired,
        username,
        email,
        emailVerified,
        familyName,
        givenName,
      } = action.payload;
      state.username = username || '';
      state.email = email || '';
      state.emailVerified = emailVerified || true;
      state.familyName = familyName || '';
      state.givenName = givenName || '';
      state.idToken = idToken || '';
      state.accessToken = accessToken || '';
      state.preferredUsername = preferredUsername || '';
      state.newPasswordRequired = newPasswordRequired || false;
    });
    // logout user thunk fulfilled
    builder.addCase(logoutUserThunk.fulfilled, () => {
      return initialState;
    });
    builder.addMatcher(
      (action: Action) =>
        (action.type as string).startsWith(ACTIONS_PREFIX) &&
        (action.type as string).endsWith('/fulfilled'),
      (state) => {
        Object.assign(state, {
          isLoading: false,
          status: 'success',
        });
      },
    );
    builder.addMatcher(
      (action: Action) =>
        (action.type as string).startsWith(ACTIONS_PREFIX) &&
        (action.type as string).endsWith('/pending'),
      (state) => {
        Object.assign(state, {
          isLoading: true,
          status: 'loading',
        });
      },
    );
    builder.addMatcher(
      (action: UnknownAction) =>
        (action.type as string).startsWith(ACTIONS_PREFIX) &&
        (action.type as string).endsWith('/rejected'),
      (state, action) => {
        Object.assign(state, {
          isLoading: false,
          status: 'failure',
          error: action || '',
        });
      },
    );
  },
});

// SELECTORS
export const selectAuthState = (state: RootState) => state.auth;
// ACTIONS
export const { setCredentials, setInactivity, clearAuthState } = authSlice.actions;
// REDUCER
export default authSlice.reducer;
